import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import paidStamp from "images/paid_stamp.png";
import { ReactComponent as Logo } from "images/rs-logo.svg";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { USDollar } from "utils/helpers";
import { EditPriceDialog } from "./EditPriceDialog";
import { InvoiceToPdfButton } from "./InvoiceToPdfButton";
import * as SC from "./RecipientDetails.style";
import { useAuthenticator } from "auth/useAuthenticator";

const Container = styled(Stack)`
  width: 100%;
  padding: 30px;
  gap: 1rem;
`;

const HeaderData = styled(Box)`
  width: 300px;
  font-size: 14px;
`;

const Recipient = styled(Box)`
  width: 100%;
  font-size: 16px;
`;

const TableBox = styled(Box)`
  border: thin solid lightgray;
`;

export const InvoiceDetails = () => {
  const { handleAuthError } = useAuthenticator();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { userData, showWarning, openConfirmationWithCallback } =
    useContext(GlobalContext);
  const { createApiCall } = useCreateApiCall();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [currentRecord, setCurrentRecord] = useState(null);

  const getData = createApiCall(async () => {
    // load recipient details by recipient id
    const result = await API.get("restApi", `/invoices/${id}`, {});

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    setData(result);
  });

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDataPresent = !!data?.invoice;
  const isTexas = data?.recipient?.state === "TX";
  const isPaid = data?.invoice?.total_amount > 0 && data?.invoice?.paymentStatus === "Paid";
  const positions = data?.invoiceItems ?? [];
  const taxes = data?.invoiceTaxes ?? [];

  const subTotal = positions.reduce(
    (acc, { total_price, total_price_adjusted }) =>
      acc + (Number(total_price_adjusted) > 0 ? Number(total_price_adjusted) : Number(total_price)),
    0
  );
  const invoiceTotal = Number(data?.invoice?.total_amount ?? 0);

  const date = new Date(data?.invoice?.invoice_date);
  const invoiceDate = isDataPresent
    ? new Intl.DateTimeFormat("en-US", {
        dateStyle: "short",
      }).format(date)
    : "";
  date.setDate(date.getDate() + 7);
  const invoiceDueDate = isDataPresent
    ? new Intl.DateTimeFormat("en-US", {
        dateStyle: "short",
      }).format(date)
    : "";

  const isSuperAdmin = userData?.role === "Super Admin" || userData?.email === "admin@referral-stream.com";

  const handleDeleteInvoice = () =>
    openConfirmationWithCallback({
      title: "Attention",
      message: "This deletion cannot be undone. Proceed?",
      buttonLabels: ["No", "Yes"],
      callbackFn: async () => {
        try {
          const result = await API.del("restApi", `/invoices/${id}`, {});

          if (result?.error) {
            console.error(result.error);
            dispatch(showError(result.error));
            return;
          }

          showWarning({
            title: (
              <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {`Invoice #${id} deleted successfully`}
                </Typography>
              </Stack>
            ),
            content: <></>,
            asNotification: true,
          });
          navigate("/billing");
        } catch (error) {
          handleAuthError(error);
        }
      },
    });

  const handleSendInvoice = createApiCall(async () => {
    if (!id) return;
    try {
      const result = await API.get("restApi", `/send_invoice/${id}`, {});

      if (result?.error) {
        console.error(result.error);
        dispatch(showError(result.error));
        return;
      }

      showWarning({
        title: (
          <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {`Invoice #${id} sent successfully`}
            </Typography>
          </Stack>
        ),
        content: <></>,
        asNotification: true,
      });
    } catch (error) {
      handleAuthError(error);
    }
  });

  return (
    <CommonOutletTemplate backIcon backLink={-1}>
      {isDataPresent && (
        <Stack alignItems={"center"} minWidth={"768px"}>
          {/* <div className="fixed top-20 left-4 w-full block lg:hidden mb-2">
            <BackToPrevPage />
          </div> */}
          <SC.Paper flex={1} sx={{ minHeight: "800px", maxWidth: "1024px" }}>
            <Container>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"flex-start"}
              >
                <Logo />

                <Stack direction={"row"} sx={{ position: "relative" }} gap={1}>
                  {isSuperAdmin && !isPaid && (
                    <Button
                      variant={"contained"}
                      color={"warning"}
                      sx={{ padding: "0 10px" }}
                      onClick={handleDeleteInvoice}
                    >
                      Delete
                    </Button>
                  )}
                  {isPaid && (
                    <img
                      width="200"
                      src={paidStamp}
                      alt="intro slide"
                      style={{
                        top: 30,
                        right: "220px",
                        position: "absolute",
                      }}
                    />
                  )}
                  <InvoiceToPdfButton data={data} />
                  <Button
                    variant={"contained"}
                    color={"primary"}
                    sx={{ padding: "0 10px" }}
                    onClick={handleSendInvoice}
                  >
                    Send
                  </Button>
                </Stack>
              </Stack>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <HeaderData>
                  Referral Stream Solutions, LLC
                  <br />
                  5900 Balcones Drive
                  <br />
                  Suite 400
                  <br />
                  Austin, TX 78731
                </HeaderData>
                <HeaderData textAlign={"right"}>
                  Invoice #{id}
                  <br />
                  Invoice Date: {invoiceDate}
                  <br />
                  Due Date: {invoiceDueDate}
                  <br />
                  Total Due: {USDollar.format(invoiceTotal)}
                </HeaderData>
              </Stack>
              <Recipient mt={4}>
                Bill to:
                <br />
                {data?.recipient?.name}, {data?.recipient?.street}
                {data?.recipient?.street2
                  ? `, ${data?.recipient?.street2}`
                  : ""}
                , {data?.recipient?.city}, {data?.recipient?.state},{" "}
                {data?.recipient?.zipCode}
                <br />
                {/* phone: {data?.recipient?.phone} */}
              </Recipient>
              <TableContainer component={TableBox} mt={5}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#225DA4" }}>
                      <TableCell align="left" sx={{ color: "white" }}>
                        Description
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white" }}>
                        Quantity
                      </TableCell>
                      <TableCell align="right" sx={{ color: "white" }}>
                        Price
                      </TableCell>
                      <TableCell align="right" sx={{ color: "white" }}>
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {positions?.map((record) => {
                      const {
                        id,
                        item_description,
                        quantity,
                        unit_price,
                        unit_price_adjusted,
                        total_price,
                        total_price_adjusted,
                      } = record;
                      const price = Number(unit_price ?? 0);
                      const priceAdjusted = Number(unit_price_adjusted ?? 0);

                      const totalPrice = Number(total_price ?? 0);
                      const totalPriceAdjusted = Number(
                        total_price_adjusted ?? 0
                      );

                      return (
                        <TableRow key={`invoice-position-${id}`}>
                          <TableCell align="left">{item_description}</TableCell>
                          <TableCell align="center">{quantity}</TableCell>
                          <TableCell align="center">
                            <Stack
                              direction={"row"}
                              justifyContent={"flex-end"}
                              alignItems={"center"}
                            >
                              {priceAdjusted > 0 && price > 0 && (
                                <Stack>
                                  <Box sx={{ textDecoration: "line-through" }}>
                                    {USDollar.format(price)}
                                  </Box>
                                  <Box>{USDollar.format(priceAdjusted)}</Box>
                                </Stack>
                              )}
                              {priceAdjusted === 0 && price > 0 && (
                                <Box>{USDollar.format(price)}</Box>
                              )}
                              {priceAdjusted > 0 && price === 0 && (
                                <Box>{USDollar.format(priceAdjusted)}</Box>
                              )}
                              {priceAdjusted === 0 && price === 0 && (
                                <Box>{USDollar.format(0)}</Box>
                              )}
                              {isSuperAdmin && !isPaid && (
                                <IconButton
                                  onClick={() => setCurrentRecord(record)}
                                >
                                  <EditIcon />
                                </IconButton>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            {totalPriceAdjusted > 0 && totalPrice > 0 && (
                              <Stack>
                                <Box sx={{ textDecoration: "line-through" }}>
                                  {USDollar.format(totalPrice)}
                                </Box>
                                <Box>{USDollar.format(totalPriceAdjusted)}</Box>
                              </Stack>
                            )}
                            {totalPriceAdjusted === 0 && totalPrice > 0 && (
                              <Box>{USDollar.format(totalPrice)}</Box>
                            )}
                            {totalPriceAdjusted > 0 && totalPrice === 0 && (
                              <Box>{USDollar.format(totalPriceAdjusted)}</Box>
                            )}
                            {totalPriceAdjusted === 0 && totalPrice === 0 && (
                              <Box>{USDollar.format(0)}</Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!positions?.length && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{ padding: "2rem" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    )}
                    {isTexas && (
                      <>
                        <TableRow>
                          <TableCell colSpan={3} align="right">
                            Sub Total:
                          </TableCell>
                          <TableCell align="right">
                            {USDollar.format(subTotal)}
                          </TableCell>
                        </TableRow>
                        {taxes?.map(({ description, rate, amount }) => (
                          <TableRow key={description}>
                            <TableCell colSpan={3} align="right">
                              {description} ({rate}%):
                            </TableCell>
                            <TableCell align="right">
                              {USDollar.format(amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                    <TableRow>
                      <TableCell colSpan={3} align="right">
                        Invoice Total:
                      </TableCell>
                      <TableCell align="right">
                        {USDollar.format(invoiceTotal)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Container>
          </SC.Paper>
        </Stack>
      )}
      <EditPriceDialog
        open={!!currentRecord}
        invoice={data?.invoice}
        record={currentRecord}
        handleClose={() => setCurrentRecord(null)}
        refreshFn={() => getData()}
      />
    </CommonOutletTemplate>
  );
};
